import * as Popper from "@popperjs/core";
import clsx from "clsx";
import React, { useLayoutEffect, useRef, useState } from "react";
import DatePicker, { ReactDatePickerCustomHeaderProps } from "react-datepicker";
import IconAdorner from "../../../components/icon/IconAdorner";
import { useTheme } from "../../../hooks";
import { Typography } from "../../../layout";
import {
  AcceptedDateTypes,
  BaseInputProps,
  DateInputThemeType,
  MonthsValueOptions,
  NotApplicable,
  defaultAsSelectPlaceholder,
  defaultMonthsValueOptions,
} from "../../../types";
import {
  DATE_FORMATTER_STR,
  DATE_FORMATTER_STR_WITH_TIME,
  DATE_FORMAT_STR,
  DATE_FORMAT_STR_WITH_TIME,
  TIME_FORMATTER_STR,
  TIME_FORMAT_STR,
  formatDate,
  getEnrichedDate,
  getRawDate,
  isDateType,
  isNA,
} from "../../../utils";
import { getElement } from "../../../utils/DocumentUtils";
import ValidationMessages from "../../validation/ValidationMessages";
import Input from "../input/Input";
import Label from "../label/Label";
import DateInputAsSelects from "./DateAsSelectsInput";
import { DatePickerStylesWrapper, DateRoot } from "./DateInputStyles";
import { createDateTheme } from "./DateThemeUtil";

const portalId = "global-datepicker-mount";

export type AsSelectPlaceholders = {
  day: string;
  month: string;
  year: string;
};

export type DateInputProps<DateType extends AcceptedDateTypes> = BaseInputProps & {
  value?: DateType | NotApplicable;
  onChange: (value?: DateType | NotApplicable) => void;
  displayFormat?: string;
  dateFormat?: string;
  minDate?: AcceptedDateTypes;
  maxDate?: AcceptedDateTypes;
  yearOnly?: boolean;
  showTimeSelect?: boolean;
  showTimeSelectOnly?: boolean;
  timeIntervals?: number;
  asSelects?: boolean;
  asSelectsPlaceholders?: AsSelectPlaceholders;
  monthsValueOptions?: MonthsValueOptions;
  styles?: DateInputThemeType;
  inline?: boolean;
  invalidDateMessage?: string;
  clearable?: boolean;
  renderCustomHeader?: (params: ReactDatePickerCustomHeaderProps) => React.ReactNode;
  popoverPlacement?: Popper.Placement;
  withPortal?: boolean;
};

export const DateInput = <DateType extends AcceptedDateTypes>(props: DateInputProps<DateType>) => {
  const {
    id,
    value,
    onChange,
    placeholder,
    displayFormat,
    dateFormat,
    label,
    className = "",
    yearOnly = false,
    required = false,
    disabled = false,
    readOnly = false,
    missed = false,
    minDate,
    maxDate,
    showTimeSelect = false,
    showTimeSelectOnly = false,
    timeIntervals = 15,
    asSelects = false,
    asSelectsPlaceholders = defaultAsSelectPlaceholder,
    monthsValueOptions = defaultMonthsValueOptions,
    styles,
    gutterBottom = false,
    inline = false,
    invalidDateMessage = "Invalid Date",
    showNA = false,
    clearable = false,
    popoverPlacement = "bottom",
    withPortal = false,
    inputSize = "md",
    renderCustomHeader,
  } = props;
  const { Theme } = useTheme();
  const StylesOverride: DateInputThemeType = { ...Theme.datepicker, ...styles };
  const datePicker = useRef<DatePicker | null>(null);
  const [validDate, setValidDate] = useState<boolean>(true);

  const onChangeValue = (newValue: Date | undefined | NotApplicable) => {
    onChange(getEnrichedDate(newValue, value, dateFormat));
  };

  const iconClicked = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!!datePicker.current) {
      datePicker.current.setOpen(true);
    }
  };

  const finalDateFormat = !!yearOnly
    ? "yyyy"
    : !!displayFormat
    ? displayFormat
    : showTimeSelect
    ? showTimeSelectOnly
      ? TIME_FORMAT_STR
      : DATE_FORMAT_STR_WITH_TIME
    : DATE_FORMAT_STR;
  const isInvalid = !!(className && className.includes("is-invalid"));

  const onChangeDatePicker = (value: Date | null) => {
    onChangeValue(!!value ? value : undefined);
  };

  const toggleNA = (value: boolean) => {
    if (value) {
      onChangeValue("N/A");
    } else {
      if (asSelects) {
        onChangeValue(new Date());
      } else {
        onChangeValue(undefined);
      }
    }
  };

  const isDate: boolean = isDateType(value);
  const isNAValue: boolean = !isDate && isNA(showNA, value);

  useLayoutEffect(() => {
    if (withPortal) {
      const element = getElement(portalId);
      if (!element) {
        const portalElement = document.createElement("div");
        portalElement.id = portalId;
        document.body.prepend(portalElement);
      }

      const styleId = `style-${portalId}`;
      const styleTheme = createDateTheme(StylesOverride);
      let styleElement = getElement(styleId);
      if (!!styleElement) {
        styleElement.remove();
      }
      styleElement = document.createElement("style");
      styleElement.id = styleId;
      styleElement.appendChild(document.createTextNode(styleTheme));
      document.body.prepend(styleElement);
    }
  }, [withPortal, Theme]);

  const DatePickerElement = (
    <div id="best-datepicker" className={clsx({ "mb-2": gutterBottom })}>
      <DatePickerStylesWrapper styles={StylesOverride}>
        <DatePicker
          ref={datePicker}
          id={id}
          selected={getRawDate(value, dateFormat)}
          onChange={onChangeDatePicker}
          required={required}
          showTimeSelect={showTimeSelect}
          showTimeSelectOnly={showTimeSelectOnly}
          timeIntervals={timeIntervals}
          dateFormat={finalDateFormat}
          popperPlacement={popoverPlacement}
          disabled={disabled || isNAValue}
          className={className}
          peekNextMonth
          showMonthDropdown
          placeholderText={placeholder}
          showYearDropdown={!yearOnly}
          showYearPicker={yearOnly}
          dropdownMode="select"
          minDate={getRawDate(minDate, dateFormat)}
          maxDate={getRawDate(maxDate, dateFormat)}
          inline={inline}
          isClearable={clearable}
          clearButtonClassName="date-clearable"
          renderCustomHeader={renderCustomHeader}
          portalId={withPortal ? portalId : undefined}
          customInput={
            <Input
              id={`date-input-${id}`}
              onChange={() => {}}
              value=""
              failedRequired={isInvalid}
              readOnly={false}
              missed={missed}
              required={required}
              inputSize={inputSize}
              placeholder={placeholder}
              styles={{
                input: {
                  focused: StylesOverride.input.focused,
                },
              }}
              endAdorner={
                <IconAdorner
                  iconName={showTimeSelectOnly ? "fa-clock" : "fa-calendar-alt"}
                  styles={{
                    color: StylesOverride.input.actionColor,
                    hoverColor: StylesOverride.input.actionHoverColor,
                  }}
                  onClick={iconClicked}
                  disabled={disabled}
                />
              }
            />
          }
        />
      </DatePickerStylesWrapper>
    </div>
  );

  return (
    <DateRoot className={clsx("w-100", { "mb-2": gutterBottom })}>
      {!!label && (
        <Label
          htmlFor={id}
          required={required}
          missed={missed}
          showNA={showNA && !readOnly}
          isNA={isNAValue}
          onChange={toggleNA}
        >
          {label}
        </Label>
      )}
      <ValidationMessages
        validationMessages={[
          {
            type: "error",
            message: invalidDateMessage,
            validationFunction: () => !validDate,
          },
        ]}
      />
      {readOnly ? (
        <Typography id={id}>
          {isDate
            ? formatDate(
                value as Date,
                showTimeSelect
                  ? showTimeSelectOnly
                    ? TIME_FORMATTER_STR
                    : DATE_FORMATTER_STR_WITH_TIME
                  : DATE_FORMATTER_STR,
              )
            : value?.toString()}
        </Typography>
      ) : !asSelects ? (
        DatePickerElement
      ) : (
        <DateInputAsSelects
          id={id}
          value={value}
          onChange={onChange}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          asSelectsPlaceholders={asSelectsPlaceholders}
          monthsValueOptions={monthsValueOptions}
          clearable={clearable}
          setValidDate={setValidDate}
          required={required}
        />
      )}
    </DateRoot>
  );
};

export default DateInput;
